"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu"
import { Share2, Mail, MessageCircle, Copy, ExternalLink } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useLanguage } from "@/contexts/language-context"

interface ShareButtonProps {
  text: string
  className?: string
  size?: "sm" | "default" | "lg"
  variant?: "outline" | "default" | "ghost"
}

export function ShareButton({ 
  text, 
  className = "", 
  size = "sm",
  variant = "outline"
}: ShareButtonProps) {
  const { toast } = useToast()
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)

  // 编码文本用于URL
  const encodedText = encodeURIComponent(text)
  const encodedTextWithSource = encodeURIComponent(`${text}\n\n— Generated by Communicate to Win`)

  // 分享到邮件
  const shareToEmail = () => {
    const subject = encodeURIComponent("AI Generated Response")
    const body = encodedTextWithSource
    window.open(`mailto:?subject=${subject}&body=${body}`)
    setIsOpen(false)
  }

  // 分享到WhatsApp
  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedTextWithSource}`)
    setIsOpen(false)
  }

  // 分享到Telegram
  const shareToTelegram = () => {
    window.open(`https://t.me/share/url?text=${encodedTextWithSource}`)
    setIsOpen(false)
  }

  // 分享到Twitter/X
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodedTextWithSource}`)
    setIsOpen(false)
  }

  // 分享到LinkedIn
  const shareToLinkedIn = () => {
    const url = encodeURIComponent("https://communicate2win.com")
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${encodedText}`)
    setIsOpen(false)
  }

  // 分享到Facebook
  const shareToFacebook = () => {
    const url = encodeURIComponent("https://communicate2win.com")
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodedText}`)
    setIsOpen(false)
  }

  // 复制链接（包含文本）
  const copyShareLink = () => {
    const shareText = `${text}\n\n— Generated by Communicate to Win\nhttps://communicate2win.com`
    navigator.clipboard.writeText(shareText).then(() => {
      toast({
        title: t("status.copied"),
        description: t("interface.shareTextCopied"),
      })
    }).catch(() => {
      // 备用复制方法
      const textArea = document.createElement('textarea')
      textArea.value = shareText
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast({
          title: t("status.copied"),
          description: t("interface.shareTextCopied"),
        })
      } catch (error) {
        console.error('复制失败:', error)
      } finally {
        document.body.removeChild(textArea)
      }
    })
    setIsOpen(false)
  }

  // 使用Web Share API（如果支持）
  const useNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: "AI Generated Response",
          text: text,
          url: "https://communicate2win.com"
        })
        setIsOpen(false)
      } catch (error) {
        console.log('Native share cancelled or failed:', error)
      }
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant}
          size={size}
          className={`${className}`}
        >
          <Share2 className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t("interface.shareResponse")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Native Share API (移动设备优先) */}
        {typeof window !== 'undefined' && 'share' in navigator && (
          <>
            <DropdownMenuItem onClick={useNativeShare}>
              <Share2 className="mr-2 h-4 w-4" />
              {t("interface.shareNative")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* 即时通讯应用 */}
        <DropdownMenuItem onClick={shareToWhatsApp}>
          <MessageCircle className="mr-2 h-4 w-4" />
          WhatsApp
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={shareToTelegram}>
          <MessageCircle className="mr-2 h-4 w-4" />
          Telegram
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* 社交媒体 */}
        <DropdownMenuItem onClick={shareToTwitter}>
          <ExternalLink className="mr-2 h-4 w-4" />
          Twitter/X
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={shareToLinkedIn}>
          <ExternalLink className="mr-2 h-4 w-4" />
          LinkedIn
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={shareToFacebook}>
          <ExternalLink className="mr-2 h-4 w-4" />
          Facebook
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* 邮件和复制 */}
        <DropdownMenuItem onClick={shareToEmail}>
          <Mail className="mr-2 h-4 w-4" />
          {t("interface.shareEmail")}
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={copyShareLink}>
          <Copy className="mr-2 h-4 w-4" />
          {t("interface.copyShareText")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 